import axios from 'axios';
import { CreateTaskRequest, UpdateTaskRequest, TaskArea, TaskPriority, TaskStatus } from '../types/task';
import { CreateServiceOrderRequest, UpdateServiceOrderRequest } from '../types/serviceOrder';
import config from '../config/environment';

const api = axios.create({
  baseURL: config.apiUrl,
});


api.interceptors.request.use((config) => {
  // Don't add Authorization header for auth routes
  const authRoutes = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
  const isAuthRoute = authRoutes.some(route => config.url?.includes(route));
  
  if (!isAuthRoute) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors (unauthorized) - simply redirect to login
    if (error.response?.status === 401) {
      // Don't redirect if already on auth pages
      const authPages = ['/login', '/register'];
      const isAuthPage = authPages.some(page => window.location.pathname === page);
      
      if (!isAuthPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('userCache');
        sessionStorage.clear();
        
        const logoutEvent = new CustomEvent('userLogout');
        window.dispatchEvent(logoutEvent);
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const login = (email: string, password: string, rememberMe: boolean = false) => {
  return api.post('/auth/login', { email, password, rememberMe });
};

export const register = (name: string, email: string, password: string) => {
  return api.post('/auth/register', { name, email, password });
};

export const getCurrentUser = () => {
  return api.get('/auth/me');
};

export const logout = () => {
  return api.post('/auth/logout').finally(() => {
    // Always clear local storage even if API call fails
    localStorage.removeItem('token');
    localStorage.removeItem('userCache');
    sessionStorage.clear();
  });
};

export const forgotPassword = (email: string) => {
  return api.post('/auth/forgot-password', { email });
};

export const validateResetToken = (token: string) => {
  return api.get(`/auth/reset-password/${token}`);
};

export const resetPassword = (token: string, password: string, confirmPassword: string) => {
  return api.post('/auth/reset-password', { token, password, confirmPassword });
};

export const updateUserProfile = (profileData: {
  name?: string;
  phone?: string;
  timezone?: string;
  theme?: 'light' | 'dark' | 'system';
  language?: 'pt' | 'en';
}) => {
  return api.put('/auth/profile', profileData);
};

export const updateUserPassword = (passwordData: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}) => {
  return api.put('/auth/password', passwordData);
};

export const uploadUserAvatar = (file: File) => {
  const formData = new FormData();
  formData.append('avatar', file);
  
  return api.post('/auth/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const removeUserAvatar = () => {
  return api.delete('/auth/avatar');
};

// Project API functions
interface JiraConfig {
  projectKey: string;
  jiraUrl: string;
  username: string;
  apiToken: string;
}

interface CreateProjectData {
  name: string;
  description?: string;
  jiraEnabled?: boolean;
  jiraConfig?: JiraConfig;
}

interface UpdateProjectData {
  name: string;
  description?: string;
  jiraEnabled?: boolean;
  jiraConfig?: JiraConfig;
}

export const createProject = (projectData: CreateProjectData) => {
  return api.post('/projects', projectData);
};

export const getProjects = (archived?: boolean) => {
  const params = new URLSearchParams();
  if (archived !== undefined) {
    params.append('archived', archived.toString());
  }
  
  const queryString = params.toString();
  const url = `/projects${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const getAccessibleProjects = (archived?: boolean) => {
  const params = new URLSearchParams();
  if (archived !== undefined) {
    params.append('archived', archived.toString());
  }
  
  const queryString = params.toString();
  const url = `/projects/accessible${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const getProject = (id: string) => {
  return api.get(`/projects/${id}`);
};

export const uploadProjectLogo = (projectId: string, file: File) => {
  const formData = new FormData();
  formData.append('logo', file);
  
  return api.post(`/projects/${projectId}/logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).catch(error => {
    // Handle 500 errors more gracefully
    if (error.response?.status === 500) {
      throw new Error('Funcionalidade de upload de logo ainda não está disponível no servidor. Entre em contato com o administrador.');
    }
    throw error;
  });
};

export const removeProjectLogo = (projectId: string) => {
  return api.delete(`/projects/${projectId}/logo`).catch(error => {
    // Handle 500 errors more gracefully
    if (error.response?.status === 500) {
      throw new Error('Funcionalidade de remoção de logo ainda não está disponível no servidor. Entre em contato com o administrador.');
    }
    throw error;
  });
};

export const getProjectLogoUrl = (projectId: string) => {
  return api.get(`/projects/${projectId}/logo-url`);
};

export const updateProject = (id: string, projectData: UpdateProjectData) => {
  return api.put(`/projects/${id}`, projectData);
};

export const deleteProject = (id: string) => {
  return api.delete(`/projects/${id}`);
};

export const archiveProject = (id: string) => {
  return api.patch(`/projects/${id}/archive`);
};

export const unarchiveProject = (id: string) => {
  return api.patch(`/projects/${id}/unarchive`);
};

export const updateProjectSettings = (id: string, settings: { hoursTrackingEnabled: boolean }) => {
  return api.patch(`/projects/${id}/settings`, settings);
};

// Task API functions
export const getTasks = (
  projectId: string, 
  filters?: { 
    status?: TaskStatus; 
    prioridade?: TaskPriority; 
    area?: TaskArea;
    unassigned?: boolean; 
  }
) => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.prioridade) params.append('prioridade', filters.prioridade);
  if (filters?.area) params.append('area', filters.area);
  if (filters?.unassigned !== undefined) params.append('unassigned', filters.unassigned.toString());
  
  const queryString = params.toString();
  const url = `/projects/${projectId}/tasks${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const createTask = (projectId: string, task: CreateTaskRequest) => {
  return api.post(`/projects/${projectId}/tasks`, task);
};

export const getTask = (projectId: string, taskId: string) => {
  return api.get(`/projects/${projectId}/tasks/${taskId}`);
};

export const updateTask = (projectId: string, taskId: string, task: UpdateTaskRequest) => {
  return api.put(`/projects/${projectId}/tasks/${taskId}`, task);
};

export const deleteTask = (projectId: string, taskId: string) => {
  return api.delete(`/projects/${projectId}/tasks/${taskId}`);
};

export const bulkDeleteTasks = (projectId: string, taskIds: string[]) => {
  return api.delete(`/projects/${projectId}/tasks/bulk`, { data: { taskIds } });
};

export const createTasksFromAI = (projectId: string, file: File) => {
  const formData = new FormData();
  formData.append('transcription', file);
  
  return api.post(`/projects/${projectId}/tasks/ai-create`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Service Order API functions
export const getServiceOrders = (projectId: string, filters?: { isActive?: boolean }) => {
  const params = new URLSearchParams();
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  
  const queryString = params.toString();
  const url = `/projects/${projectId}/service-orders${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const createServiceOrder = (projectId: string, serviceOrder: CreateServiceOrderRequest) => {
  return api.post(`/projects/${projectId}/service-orders`, serviceOrder);
};

export const getServiceOrder = (projectId: string, serviceOrderId: string) => {
  return api.get(`/projects/${projectId}/service-orders/${serviceOrderId}`);
};

export const updateServiceOrder = (projectId: string, serviceOrderId: string, serviceOrder: UpdateServiceOrderRequest) => {
  return api.put(`/projects/${projectId}/service-orders/${serviceOrderId}`, serviceOrder);
};

export const deleteServiceOrder = (projectId: string, serviceOrderId: string) => {
  return api.delete(`/projects/${projectId}/service-orders/${serviceOrderId}`);
};

export const assignTaskToServiceOrder = (projectId: string, serviceOrderId: string, taskId: string) => {
  return api.post(`/projects/${projectId}/service-orders/${serviceOrderId}/assign-task`, { taskId });
};

export const unassignTaskFromServiceOrder = (projectId: string, serviceOrderId: string, taskId: string) => {
  return api.post(`/projects/${projectId}/service-orders/${serviceOrderId}/unassign-task`, { taskId });
};

// Hours Bank API functions
export const getHoursBankStatus = (projectId: string) => {
  return api.get(`/projects/${projectId}/hours-bank/status`);
};

export const getHoursBank = (projectId: string, year: number, month: number) => {
  return api.get(`/projects/${projectId}/hours-bank/${year}/${month}`);
};

export const configureHoursBank = (
  projectId: string, 
  year: number, 
  month: number, 
  hours: { 
    frontend?: number; 
    backend?: number; 
    design?: number; 
    'Q&A'?: number; 
  },
  reason?: string
) => {
  return api.post(`/projects/${projectId}/hours-bank/configure`, { year, month, hours, reason });
};

// Hours Default API functions
export const getHoursDefaults = (projectId: string) => {
  return api.get(`/projects/${projectId}/hours-bank/defaults`);
};

export const setHoursDefaults = (
  projectId: string,
  defaultHours: {
    frontend?: number;
    backend?: number;
    design?: number;
    'Q&A'?: number;
  },
  reason?: string
) => {
  return api.post(`/projects/${projectId}/hours-bank/defaults`, {
    defaultHours,
    reason
  });
};

// Hours Change Log API functions
export const getHoursChangeLog = (
  projectId: string,
  params?: {
    limit?: number;
    offset?: number;
    area?: string;
    changeType?: string;
  }
) => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.area) queryParams.append('area', params.area);
  if (params?.changeType) queryParams.append('changeType', params.changeType);
  
  const queryString = queryParams.toString();
  const url = `/projects/${projectId}/hours-bank/changelog${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const getHoursBankHistory = (
  projectId: string, 
  filters?: { 
    year?: number; 
    month?: number; 
    area?: string; 
    limit?: number; 
    offset?: number; 
  }
) => {
  const params = new URLSearchParams();
  if (filters?.year) params.append('year', filters.year.toString());
  if (filters?.month) params.append('month', filters.month.toString());
  if (filters?.area) params.append('area', filters.area);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());
  
  const queryString = params.toString();
  const url = `/projects/${projectId}/hours-bank/history${queryString ? `?${queryString}` : ''}`;
  
  return api.get(url);
};

export const deleteHoursBankArea = (projectId: string, year: number, month: number, area: string) => {
  return api.delete(`/projects/${projectId}/hours-bank/${year}/${month}/${area}`);
};


// JIRA Integration API
export const configureJiraIntegration = (projectId: string, config: JiraConfig) => {
  return api.post(`/projects/${projectId}/jira/configure`, config);
};

export const removeJiraIntegration = (projectId: string) => {
  return api.delete(`/projects/${projectId}/jira/configure`);
};

export const testJiraConnection = (projectId: string) => {
  return api.post(`/projects/${projectId}/jira/test`);
};

export const getJiraSprints = (projectId: string, includeClosed: boolean = false) => {
  const params = new URLSearchParams();
  if (includeClosed) {
    params.append('includeClosed', 'true');
  }

  const queryString = params.toString();
  const url = `/projects/${projectId}/jira/sprints${queryString ? `?${queryString}` : ''}`;

  return api.get(url);
};

export const getJiraFields = (projectId: string) => {
  return api.get(`/projects/${projectId}/jira/fields`);
};

export const discoverJiraStoryPoints = (projectId: string) => {
  return api.post(`/projects/${projectId}/jira/discover-story-points`);
};

export const listJiraProjects = (projectId: string) => {
  return api.get(`/projects/${projectId}/jira/list-projects`);
};

export const exportServiceOrderToJira = (projectId: string, serviceOrderId: string, sprintId?: number) => {
  return api.post(`/projects/${projectId}/service-orders/${serviceOrderId}/export-to-jira`, {
    sprintId
  });
};

export const exportTaskToJira = (projectId: string, taskId: string) => {
  return api.post(`/projects/${projectId}/tasks/${taskId}/export-to-jira`);
};

// Debug API functions
export const getDebugUserInfo = () => {
  return api.get('/debug/user-info');
};

export const getDebugDbStatus = () => {
  return api.get('/debug/db-status');
};

export default api;